import { SocialLiveCasinoLiveStatsApiResult } from '../../services/GapApiService'
import { LiveStatsEvent, LiveStatsEvents } from './LiveStatsActions'
import {
    BaccaratGameLiveStat,
    BlackjackGameLiveStat,
    RouletteGameLiveStat,
    SlotGameLiveStat,
    SocialLiveCasinoGameLiveStat,
    SocialLiveCasinoLiveStats,
} from './LiveStatsState'

const ASSUMED_TYPE = {
    BLACKJACK: 'blackjack',
    ROULETTE: 'roulette',
    BACCARAT: 'baccarat',
    SLOT: 'slot',
} as const

const LIMITS_DIVISOR = 100

const initialState: SocialLiveCasinoLiveStats = {
}

const convertLimit = (limit: number | null): number | null => {
    if (limit === null || limit === undefined) {
        return null
    }
    return Number(limit) / LIMITS_DIVISOR
}

const mapBaccaratOutcomes = (outcomes: unknown): BaccaratGameLiveStat['latestOutcomes'] => {
    if (!outcomes) {
        return null
    }
    if (!Array.isArray(outcomes)) {
        return null
    }
    return outcomes.reduce((acc, outcome) => {
        if (typeof outcome !== 'object' || !('score' in outcome) || !('winner' in outcome)) {
            return acc
        }
        acc.push(outcome)
        return acc
    }, [] as BaccaratGameLiveStat['latestOutcomes'])
}

const mapPayloadToBlackjackLiveStat = (payload: SocialLiveCasinoLiveStatsApiResult): BlackjackGameLiveStat => {
    const blackjackLiveStat: BlackjackGameLiveStat = {
        playerCount: payload.playerCount,
        limits: {
            XSD: convertLimit(payload.limits.XSD),
            XGC: convertLimit(payload.limits.XGC),
        },
        assumedType: ASSUMED_TYPE.BLACKJACK,
        lastRoundResult: null,
        seatsAvailability: null,
    }

    if (payload.assumedType === ASSUMED_TYPE.BLACKJACK) {
        blackjackLiveStat.seatsAvailability = payload.seatsAvailability
    }

    return blackjackLiveStat
}

const mapPayloadToRouletteGameLiveStat = (payload: SocialLiveCasinoLiveStatsApiResult): RouletteGameLiveStat => {
    const rouletteGameLiveStat: RouletteGameLiveStat = {
        playerCount: payload.playerCount,
        limits: {
            XSD: convertLimit(payload.limits.XSD),
            XGC: convertLimit(payload.limits.XGC),
        },
        assumedType: ASSUMED_TYPE.ROULETTE,
        latestResults: null,
    }
    if (payload.assumedType === ASSUMED_TYPE.ROULETTE) {
        rouletteGameLiveStat.latestResults = payload.lastRoundResult.lastNumbers
    }
    return rouletteGameLiveStat
}

const mapPayloadToBaccaratGameLiveStat = (payload: SocialLiveCasinoLiveStatsApiResult): BaccaratGameLiveStat => {
    const baccaratGameLiveStat: BaccaratGameLiveStat = {
        playerCount: payload.playerCount,
        limits: {
            XSD: convertLimit(payload.limits.XSD),
            XGC: convertLimit(payload.limits.XGC),
        },
        assumedType: ASSUMED_TYPE.BACCARAT,
        latestOutcomes: null,
    }

    if (payload.assumedType === ASSUMED_TYPE.BACCARAT) {
        baccaratGameLiveStat.latestOutcomes = mapBaccaratOutcomes(payload.lastRoundResult.lastWinners)
    }

    return baccaratGameLiveStat
}

const mapPayloadToSlotGameLiveStat = (payload: SocialLiveCasinoLiveStatsApiResult): SlotGameLiveStat => {
    return {
        playerCount: payload.playerCount,
        limits: {
            XSD: convertLimit(payload.limits.XSD),
            XGC: convertLimit(payload.limits.XGC),
        },
        assumedType: ASSUMED_TYPE.SLOT,
        lastRoundResult: null,
    }
}

const mapLegacyPayloadToLiveStat = (payload: SocialLiveCasinoLiveStatsApiResult): SocialLiveCasinoGameLiveStat => {
    if (typeof payload === 'object' && 'assumedType' in payload && typeof payload.assumedType === 'string') {
        if (payload.assumedType === ASSUMED_TYPE.BLACKJACK) {
            return mapPayloadToBlackjackLiveStat(payload)
        }
        if (payload.assumedType === ASSUMED_TYPE.ROULETTE) {
            return mapPayloadToRouletteGameLiveStat(payload)
        }
        if (payload.assumedType === ASSUMED_TYPE.BACCARAT) {
            return mapPayloadToBaccaratGameLiveStat(payload)
        }
        if (payload.assumedType === ASSUMED_TYPE.SLOT) {
            return mapPayloadToSlotGameLiveStat(payload)
        }
    }

    return {
            playerCount: null,
            limits: {
                XSD: null,
                XGC: null,
            },
            assumedType: ASSUMED_TYPE.SLOT,
            lastRoundResult: null,
        }
}

function isLegacyPayload(entry: unknown): entry is SocialLiveCasinoLiveStatsApiResult {
    return typeof entry === 'object' && entry !== null && 'gameId' in entry
}

export const socialLiveCasinoStatsReducer = (
    state: SocialLiveCasinoLiveStats = initialState,
    event: LiveStatsEvent,
): SocialLiveCasinoLiveStats => {
    switch (event.type) {
        case LiveStatsEvents.UPDATE_LIVE_STATS:
            const payload = Object.entries(event.payload.liveStats as Record<string, unknown>).reduce((accumulator, [gameId, liveStatPayload]) => {
                const mapped = isLegacyPayload(liveStatPayload)
                    ? mapLegacyPayloadToLiveStat(liveStatPayload)
                    : liveStatPayload as SocialLiveCasinoGameLiveStat
                return {
                    ...accumulator,
                    [gameId]: mapped
                }
            }, {} as SocialLiveCasinoLiveStats)

            return {
                ...state,
                ...payload
            }
        default:
            return state
    }
}
